import { VerifiedUser } from '@mui/icons-material';
import { Box, Divider, Grid, Typography } from '@mui/material';
import Vehicle from '@acorn-portal/shared/src/components/display/vehicle';
import useReferenceData from '@acorn-portal/shared/src/hooks/useReferenceData';
import { FC } from 'react';
import { FullPolicy } from '@acorn-portal/shared/src/services/api';
import DateRangeDisplay from '@acorn-portal/shared/src/components/display/date-range';
import { ListNames } from '@acorn-portal/shared/src/store/referenceData.slice';
import AddressDetails from '@acorn-portal/shared/src/components/renewal/address';
import Endorsements from '@acorn-portal/shared/src/components/display/endorsements';
import Price from '@acorn-portal/shared/src/components/display/price';
import SortDrivers from '@acorn-portal/shared/src/utils/sortDrivers';
import AddOns from '@acorn-portal/shared/src/components/display/add-ons';
import DriverExcess from '@acorn-portal/shared/src/components/display/driver-excess';
import { isProtectYD } from '@acorn-portal/shared/src/utils';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import Organisation from '@acorn-portal/shared/src/components/display/organisation';

interface IProps {
	policy: FullPolicy;
}

const PolicyBreakdown: FC<IProps> = ({ policy }) => {
	const { vehicleDetails, drivers, addOns, organisationDetails } = policy;
	const { getReferenceDataValue } = useReferenceData();
	const { policyHolder, additionalDrivers } = SortDrivers(drivers);
	const isCarrot = policy.product === Product.CARROT;

	const isProtectYoungDriver: boolean = isProtectYD(policy.product, policy.scheme ?? '');

	return (
		<Box sx={{ paddingTop: 4 }}>
			<Typography sx={{ marginBottom: 2, display: 'flex', alignItems: 'center' }}>
				<VerifiedUser sx={{ marginRight: 1 }} />{' '}
				{getReferenceDataValue(ListNames.levelOfCoverTypes, policy.coverDetails.coverType)}
			</Typography>
			<Endorsements endorsements={policy.coverDetails.endorsements || []} />
			<Grid container spacing={4}>
				<Grid size={12}>
					<Price value={policy.coverDetails.premium} description='Policy Premium' />
					<Typography fontSize={10}>
						The amount shown does not include any fees
						{!isProtectYoungDriver && !isCarrot && !policy.nonFinancePolicy ? ' or interest.' : '.'}
					</Typography>
				</Grid>

				{!!policy.coverDetails.voluntaryExcess && (
					<Grid size={6}>
						<Price value={policy.coverDetails.voluntaryExcess} description='Voluntary Excess' />
					</Grid>
				)}
				{!!policy.coverDetails.compulsoryExcess && (
					<Grid size={6}>
						<Price value={policy.coverDetails.compulsoryExcess} description='Compulsory Excess' />
					</Grid>
				)}
			</Grid>
			{policy.coverDetails.coverType !== 'TPO' ? (
				<Box sx={{ borderTop: 1, borderColor: 'grey.300', mt: 4, py: 2 }}>
					<DriverExcess drivers={policy.drivers} driverExcesses={policy.coverDetails.driverExcesses} />
				</Box>
			) : (
				<Divider />
			)}
			<Grid container gap={4} sx={{ alignItems: 'flex-end', my: 4 }}>
				<Grid size={12}>
					<DateRangeDisplay start={policy.startDate} end={policy.endDate} description='Current Cover Period' />
				</Grid>
			</Grid>
			<AddOns addOns={addOns} />

			{organisationDetails ? (
				<Box
					sx={{
						borderTop: 1,
						borderColor: 'grey.300',
						mt: 0,
						p: 2,
					}}
				>
					<Organisation
						organisationName={organisationDetails.companyName}
						dateEstablished={organisationDetails.dateEstablished}
						primaryContact={organisationDetails.staffContact}
					/>
				</Box>
			) : null}

			{drivers && drivers?.length > 0 ? (
				<Box
					sx={{
						borderTop: 1,
						borderColor: 'grey.300',
						mt: 0,
						p: 2,
					}}
				>
					<AddressDetails
						name={organisationDetails?.companyName ?? `${policyHolder.firstName} ${policyHolder.lastName}`}
						address={policy.address}
						namedDrivers={additionalDrivers}
					/>
				</Box>
			) : null}

			<Box sx={{ borderTop: 1, borderColor: 'grey.300', pt: 2, px: 2 }}>
				<Vehicle
					product={policy.product}
					vehicle={{
						...vehicleDetails,
						registrationNumber: vehicleDetails.registration,
					}}
				/>
			</Box>
		</Box>
	);
};

export default PolicyBreakdown;
